import * as React from 'react';
import { AnalyticsContext } from '@atlassian/analytics-web-react';
import RecaptchaEnterprise from './recaptcha-enterprise-v2';
import { RecaptchaFormError } from './recaptcha-form-error';

/**
 * React FormHTMLAttributes defines children to be of type ReactNode but RecaptchaFormProps expects it to be a
 * function. We are omitting inheriting children type from FormHTMLAttributes to avoid this conflict.
 * https://github.com/facebook/react/issues/24604
 */
export interface RecaptchaFormProps extends Omit<
	React.FormHTMLAttributes<HTMLFormElement>,
	'children'
> {
	sitekey?: string;
	recaptchaEnabled: boolean;
	formRef?: React.RefObject<HTMLFormElement>;
	onTokenChange: (token: string | null) => void;
	children: (props: { isExecuting: boolean }) => React.ReactNode;
}

interface RecaptchaFormState {
	isExecuting: boolean;
	isError: boolean;
}

/**
 * RecaptchaForm
 *
 * This will validate against google recaptcha on form submission.
 * When the token is validated (or invalidated) it will callback
 * using props.onTokenChange() and then callback with props.onSubmit(e).
 */
export default class RecaptchaForm extends React.PureComponent<
	RecaptchaFormProps,
	RecaptchaFormState
> {
	state: RecaptchaFormState = {
		isExecuting: false,
		isError: false,
	};

	lastFormSubmissionEvent: React.FormEvent<HTMLFormElement> | undefined = undefined;
	recaptchaInstance: typeof RecaptchaEnterprise | null | undefined = undefined;

	constructor(props: RecaptchaFormProps) {
		super(props);
		// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, @atlassian/ssr-linting/no-dom-globals-in-constructor
		(window as any).recaptchaOptions = {
			useRecaptchaNet: true,
		};
	}

	onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		if (this.props.recaptchaEnabled) {
			this.handleRecaptchaOnSubmit(e);
		} else {
			this.props.onSubmit && this.props.onSubmit(e);
		}
	};

	handleRecaptchaOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.persist();
		e.preventDefault();
		this.lastFormSubmissionEvent = e;

		// If the recaptcha instance has not loaded properly then show an error

		// Suppressing existing violation. Please fix this.
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		if (this.recaptchaInstance == null || this.recaptchaInstance.getWidgetId() == null) {
			this.setState({
				isError: true,
			});
		} else {
			this.setState({
				isExecuting: true,
			});

			// Suppressing existing violation. Please fix this.
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
			this.recaptchaInstance && this.recaptchaInstance.reset();

			// Suppressing existing violation. Please fix this.
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
			this.recaptchaInstance && this.recaptchaInstance.execute();
		}
	};

	handeRecaptchaOnChange = (token: string | null) => {
		if (this.props.onSubmit && this.lastFormSubmissionEvent) {
			this.props.onTokenChange(token);
			this.props.onSubmit(this.lastFormSubmissionEvent);
		}

		this.setState({
			isExecuting: false,
		});
	};

	onError = () => {
		// This is to ensure the user can retry after an recaptcha error.
		this.setState({
			isExecuting: false,
		});
	};

	onDismissError = () => {
		this.setState({
			isError: false,
		});
	};

	render() {
		const {
			children,
			sitekey,
			recaptchaEnabled,
			formRef,
			onSubmit,
			onTokenChange: _,
			...formProps
		} = this.props;

		return (
			<AnalyticsContext
				attributes={{
					sitekey,
					recaptchaEnabled,
				}}
			>
				<form {...formProps} ref={formRef} onSubmit={this.onSubmit}>
					{children({ isExecuting: this.state.isExecuting })}

					{recaptchaEnabled && sitekey && (
						<RecaptchaEnterprise
							ref={(e: typeof RecaptchaEnterprise | null | undefined) => {
								/* eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-assignment */ /* oxlint-disable-next-line no-return-assign */
								return (this.recaptchaInstance = e);
							}}
							sitekey={sitekey}
							onChange={this.handeRecaptchaOnChange}
							size="invisible"
							onErrored={this.onError}
						/>
					)}

					{recaptchaEnabled && sitekey && this.state.isError && (
						<RecaptchaFormError onDismiss={this.onDismissError} />
					)}
				</form>
			</AnalyticsContext>
		);
	}
}
